import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  opsApi, apiError, fmtINR, fmtINR2, fmtDate, fmtNum, GRAD, FONT_HEAD,
  StatusPill, INVOICE_STATUS, Pagination, ROLE_RANK,
} from './opsApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Receipt, Send, CheckCircle2, TrendingUp } from 'lucide-react';
import Loader from '../../components/Loader';

export default function OpsRevenue({ user }) {
  const canRecordPayment = (ROLE_RANK[user?.role] || 0) >= ROLE_RANK.state_manager;
  const [rollup, setRollup] = useState(null);
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [payInvoice, setPayInvoice] = useState(null);
  const [payForm, setPayForm] = useState({ payment_method: 'bank_transfer', payment_reference: '' });
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState('invoices'); // 'invoices' or 'payments'

  const load = useCallback(() => {
    opsApi.get('/revenue/rollup').then((r) => setRollup(r.data));
    if (activeTab === 'invoices') {
      opsApi.get('/invoices', { params: { page, page_size: 25, status: filter || undefined } })
        .then((r) => setData(r.data));
    } else {
      opsApi.get('/payments', { params: { page, page_size: 25, status: filter || undefined } })
        .then((r) => setPayments(r.data));
    }
  }, [page, filter, activeTab]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (inv, status, extra = {}) => {
    setBusy(true);
    try {
      await opsApi.post(`/invoices/${inv.id}/status`, { status, ...extra });
      toast.success(`Invoice marked ${status}`);
      setPayInvoice(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="ops-revenue-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={FONT_HEAD}>Revenue &amp; Billing</h1>
        <p className="text-sm text-slate-500 mt-1">Auto-rollups rep → state → zone → India · invoices &amp; collections</p>
      </div>

      {rollup && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              ['Total invoiced', rollup.total.invoiced, '#34A853'],
              ['Collected', rollup.total.collected, '#34A853'],
              ['Outstanding', rollup.total.outstanding, '#dc2626'],
              ['Refunded', rollup.total.refunded, '#7c3aed'],
            ].map(([label, value, color]) => (
              <div key={label} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`revenue-kpi-${label.toLowerCase().replace(/ /g, '-')}`}>
                <div className="h-1" style={{ background: GRAD }} />
                <div className="p-5">
                  <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">{label}</div>
                  <div className="text-2xl font-semibold tabular-nums" style={{ ...FONT_HEAD, color }}>{fmtINR(value)}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 text-sm font-semibold flex items-center gap-2" style={FONT_HEAD}>
                <TrendingUp className="w-4 h-4 text-[#3B82F6]" /> Geographic rollup
              </div>
              <table className="w-full text-sm" data-testid="rollup-by-path">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs text-slate-400 uppercase tracking-wide">
                    <th className="px-5 py-2.5 font-medium">Territory</th>
                    <th className="px-4 py-2.5 font-medium text-right">Invoiced</th>
                    <th className="px-4 py-2.5 font-medium text-right">Collected</th>
                    <th className="px-4 py-2.5 font-medium text-right">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {rollup.by_path.length === 0 && <tr><td colSpan="4" className="px-5 py-6 text-sm text-slate-400 text-center">No invoices yet.</td></tr>}
                  {rollup.by_path.map((row) => (
                    <tr key={row.path} className="border-b border-slate-50">
                      <td className="px-5 py-3" style={{ paddingLeft: `${20 + (row.depth - 1) * 16}px` }}>
                        <span className="font-medium capitalize">{row.name}</span>
                        <span className="ml-2 text-[10px] font-mono text-[#3B82F6]">{row.path}</span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtINR(row.invoiced)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#34A853]">{fmtINR(row.collected)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-red-500">{fmtINR(row.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 text-sm font-semibold" style={FONT_HEAD}>Rollup by rep</div>
              <table className="w-full text-sm" data-testid="rollup-by-rep">
                <tbody>
                  {rollup.by_rep.length === 0 && <tr><td className="px-5 py-6 text-sm text-slate-400 text-center">No invoices yet.</td></tr>}
                  {rollup.by_rep.map((row) => (
                    <tr key={row.rep_id} className="border-b border-slate-50">
                      <td className="px-5 py-3 font-medium">{row.name}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtINR(row.invoiced)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#34A853]">{fmtINR(row.collected)}</td>
                      <td className="px-4 py-3 text-right text-xs text-slate-400">{fmtNum(row.invoices)} inv</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <div className="flex gap-1.5 flex-wrap">
        <button onClick={() => { setActiveTab('invoices'); setPage(1); setFilter(''); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
            activeTab === 'invoices' ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
          }`}
          style={activeTab === 'invoices' ? { background: GRAD } : undefined}>
          Invoices
        </button>
        <button onClick={() => { setActiveTab('payments'); setPage(1); setFilter(''); }}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
            activeTab === 'payments' ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
          }`}
          style={activeTab === 'payments' ? { background: GRAD } : undefined}>
          Payments
        </button>
      </div>

      {activeTab === 'invoices' && (
        <div className="flex gap-1.5 flex-wrap">
          {['', 'draft', 'sent', 'paid', 'overdue', 'refunded', 'cancelled'].map((s) => (
            <button key={s || 'all'} data-testid={`invoice-filter-${s || 'all'}`} onClick={() => { setFilter(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize transition-colors ${
                filter === s ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
              style={filter === s ? { background: GRAD } : undefined}>
              {s || 'All invoices'}
            </button>
          ))}
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="flex gap-1.5 flex-wrap">
          {['', 'created', 'captured', 'failed'].map((s) => (
            <button key={s || 'all'} onClick={() => { setFilter(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border capitalize transition-colors ${
                filter === s ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
              style={filter === s ? { background: GRAD } : undefined}>
              {s || 'All payments'}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {activeTab === 'invoices' ? (
          !data ? (
            <Loader />
          ) : data.items.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Receipt className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No invoices. Generate them from an institution's Billing tab.</p>
            </div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs text-slate-400 uppercase tracking-wide">
                    <th className="px-5 py-3 font-medium">Invoice</th>
                    <th className="px-4 py-3 font-medium">Institution</th>
                    <th className="px-4 py-3 font-medium">Tier</th>
                    <th className="px-4 py-3 font-medium text-right">Licences</th>
                    <th className="px-4 py-3 font-medium text-right">Total</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((inv) => (
                    <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_number}`} className="border-b border-slate-50">
                      <td className="px-5 py-3.5">
                        <div className="font-mono text-xs font-medium">{inv.invoice_number}</div>
                        <div className="text-[10px] text-slate-400">{fmtDate(inv.created_at)} · due {fmtDate(inv.due_date)}</div>
                      </td>
                      <td className="px-4 py-3.5">
                        <Link to={`/sales/institutions/${inv.institution_id}`} className="text-slate-700 hover:text-[#3B82F6] font-medium">{inv.institution_name}</Link>
                      </td>
                      <td className="px-4 py-3.5 text-xs text-slate-500">{inv.tier_name || '—'}</td>
                      <td className="px-4 py-3.5 text-right text-xs text-slate-500 tabular-nums">{inv.teachers_billed}T · {inv.students_billed}S</td>
                      <td className="px-4 py-3.5 text-right font-semibold tabular-nums">{fmtINR2(inv.total)}</td>
                      <td className="px-4 py-3.5"><StatusPill value={inv.status} map={INVOICE_STATUS} /></td>
                      <td className="px-4 py-3.5 text-right">
                        <div className="flex justify-end gap-1.5">
                          {inv.status === 'draft' && (
                            <Button size="sm" variant="outline" disabled={busy} onClick={() => setStatus(inv, 'sent')} className="h-7 text-xs rounded-lg">
                              <Send className="w-3 h-3 mr-1" /> Send
                            </Button>
                          )}
                          {canRecordPayment && ['draft', 'sent', 'overdue'].includes(inv.status) && (
                            <Button size="sm" data-testid={`record-payment-${inv.invoice_number}`} disabled={busy}
                              onClick={() => { setPayInvoice(inv); setPayForm({ payment_method: 'bank_transfer', payment_reference: '' }); }}
                              className="h-7 text-xs rounded-lg text-white border-0" style={{ background: GRAD }}>
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Record payment
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination data={data} onPage={setPage} />
            </>
          )
        ) : (
          // Payments tab
          !payments ? (
            <Loader />
          ) : payments.items.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Receipt className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No payments yet. Institutions pay via Razorpay from their Billing tab.</p>
            </div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs text-slate-400 uppercase tracking-wide">
                    <th className="px-5 py-3 font-medium">Payment ID</th>
                    <th className="px-4 py-3 font-medium">Tenant ID</th>
                    <th className="px-4 py-3 font-medium">Amount</th>
                    <th className="px-4 py-3 font-medium">Currency</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.items.map((p) => (
                    <tr key={p.id} className="border-b border-slate-50">
                      <td className="px-5 py-3.5">
                        <div className="font-mono text-xs font-medium">{p.order_id || p.id}</div>
                        {p.payment_id && <div className="text-[10px] text-slate-400">Razorpay: {p.payment_id}</div>}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-xs text-slate-500">{p.tenant_id?.substring(0, 12)}...</td>
                      <td className="px-4 py-3.5 font-semibold tabular-nums">{fmtINR2(p.amount)}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-500">{p.currency}</td>
                      <td className="px-4 py-3.5">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          p.status === 'captured' ? 'bg-green-100 text-green-700' :
                          p.status === 'created' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-xs text-slate-500">{fmtDate(p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination data={payments} onPage={setPage} />
            </>
          )
        )}
      </div>

      <Dialog open={!!payInvoice} onOpenChange={(o) => !o && setPayInvoice(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={FONT_HEAD}>Record payment</DialogTitle>
            <DialogDescription>
              {payInvoice && <>Mark <b>{payInvoice.invoice_number}</b> ({fmtINR2(payInvoice.total)}) as paid by {payInvoice.institution_name}.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Payment method</Label>
              <Select value={payForm.payment_method} onValueChange={(v) => setPayForm((f) => ({ ...f, payment_method: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank transfer / NEFT</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reference / UTR number</Label>
              <Input data-testid="payment-reference-input" value={payForm.payment_reference}
                onChange={(e) => setPayForm((f) => ({ ...f, payment_reference: e.target.value }))} className="mt-1" placeholder="Optional" />
            </div>
            <Button data-testid="confirm-payment-btn" disabled={busy} onClick={() => setStatus(payInvoice, 'paid', payForm)}
              className="w-full text-white rounded-xl border-0" style={{ background: GRAD }}>
              {busy ? 'Saving…' : 'Confirm payment received'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
